import { Country } from 'country-state-city';
import { IProduct } from 'src/types/product';
import { getCookie, setCookieWithExpiry } from 'src/utils/cookies';
import { isUpsellPage } from 'src/utils/navigation';
import {
  appendUrlParams,
  getUrlParameters,
  removeUrlParams,
} from 'src/utils/url';

import { Api } from '../../apis/api';
import { Gtm } from '../../apis/gtm';
import { ALLOWED_COUNTRIES } from '../../constants/allowedCountries';
import { KlarnaAvailablePayment } from '../../types/api';
import { IFomo } from '../../types/fomo';
import { isMobile } from '../../utils/device';
import {
  addDataParamsToElement,
  elementIsInvisible,
  getVisibleElementById,
} from '../../utils/dom';
import {
  createFomoNotification,
  createProductsFomoNotification,
} from '../../utils/fomo';
import { capitalize } from '../../utils/string';

interface OfferCardSelectorProps {
  indexAttribute: string;
  selectedIdx?: number;
  onRemoveSelected: (c: HTMLElement) => void;
  onStyleSelected: (c: HTMLElement) => void;
}

export class UiController {
  static softenCtas = () => {
    const ctaTexts = document.querySelectorAll('#buttonContent');
    ctaTexts.forEach(btn => {
      if (btn.textContent) {
        btn.textContent = 'Yes, Add To My Order';
      }
    });
  };

  static getPhoneNumber = (componentId = 'phoneNumber') => {
    const phoneInput = document.getElementById(componentId) as HTMLInputElement;
    if (phoneInput && window.intlTelInputGlobals) {
      try {
        return (
          window.intlTelInputGlobals
            .getInstance(phoneInput as HTMLInputElement)
            .getNumber()
            ?.replace(/\D/g, '') || undefined
        );
      } catch {
        return phoneInput?.value?.replace(/\D/g, '') || undefined;
      }
    }
    return phoneInput?.value?.replace(/\D/g, '') || undefined;
  };

  static addSkipAfterFirstCta = (href: string) => {
    UiController.removeFirstSkipLink();
    const firstCta = document.querySelector('#buyButton');
    if (firstCta) {
      const skipLink = document.createElement('a');
      skipLink.textContent = 'I want to skip this one-time offer';
      skipLink.href = href;
      skipLink.style.color = '#818997';
      skipLink.style.fontSize = '1rem';
      skipLink.style.lineHeight = '1.3';
      skipLink.className = 'codegen-skip-link';
      skipLink.id = isUpsellPage(1) ? 'downsellOpenButton' : 'skipLink';
      firstCta.insertAdjacentElement('afterend', skipLink);
    }
  };

  static removeFirstSkipLink = () => {
    const firstCta = document.querySelector('#buyButton')?.parentElement;
    const skipLinks = firstCta?.querySelectorAll<HTMLAnchorElement>(
      '#downsellOpenButton, [data-translation="skipLink"]',
    );
    if (skipLinks) {
      skipLinks.forEach(link => {
        link.remove();
      });
    }
  };

  static changeLayout = (href: string) => {
    const utmSource = getUrlParameters('utm_source', '').toLowerCase();
    if (utmSource !== 'test' && window.GEO.country === 'US') {
      const aggressiveSources = {
        mobile: [
          'outbrain',
          'facebook',
          'tiktok',
          'email',
          'sms',
          'google',
          'bing',
        ],
        desktop: ['email', 'sms'],
      };
      const aggressive = isMobile()
        ? aggressiveSources.mobile.includes(utmSource)
        : aggressiveSources.desktop.includes(utmSource);

      if (aggressive) {
        UiController.removeFirstSkipLink();
        appendUrlParams({ utm_skip: 'off' });
      } else {
        UiController.softenCtas();
        UiController.addSkipAfterFirstCta(href);
        appendUrlParams({ utm_skip: 'on' });
      }
    } else {
      removeUrlParams('utm_skip');
    }
  };

  static populateOneClickProductCard = (product: IProduct) => {
    const images = document.querySelectorAll<HTMLImageElement>('#productImg');
    if (images.length > 0) {
      images.forEach(img => {
        img.src = product.image;
      });
    }
    if (window['productName']) {
      window['productName'].textContent = product.name;
    }
    if (window['fullPrice']) {
      window['fullPrice'].textContent = product.fullPrice.toFixed(2);
    }
    if (window['finalPrice']) {
      window['finalPrice'].textContent = product.finalPrice.toFixed(2);
    }
    if (window['discount']) {
      window['discount'].textContent = product.discount;
    }
    if (window['discountAmount']) {
      window['discountAmount'].textContent = parseFloat(
        product.discountAmount,
      ).toFixed(2);
    }
  };

  static removeInvisibleElements = () => {
    document.querySelectorAll('[data-remove-on-visibility]').forEach(e => {
      if (elementIsInvisible(e as HTMLElement)) {
        e.remove();
      }
    });
  };

  static injectLeadName = (selectorId: string, firstName: string) => {
    const span = getVisibleElementById<'span'>(selectorId);
    if (span) {
      span.textContent = capitalize(firstName);
    }
  };

  static appendOnsiteLinksWithParams = (params: string) => {
    document.querySelectorAll('a').forEach(link => {
      if (link.id !== 'buyButton') {
        const linkHref = link.getAttribute('href');
        if (
          linkHref &&
          !linkHref.startsWith('mailto:') &&
          !linkHref.startsWith('tel:')
        ) {
          // Check if link already has parameters
          const separator = linkHref.includes('?') ? '&' : '?';
          link.setAttribute('href', `${linkHref}${separator}${params}`);
        }
      }
    });
  };

  static handleExpressPaymentButtons = (
    showApplePay: boolean,
    showGooglePay: boolean,
  ) => {
    if (showApplePay) {
      const appleButton = document.querySelector(
        '#apple-pay-container',
      ) as HTMLElement;

      if (appleButton) {
        appleButton.style.display = 'flex';
        appleButton.style.width = '100%';
      }
    }

    if (showGooglePay) {
      const googleButton = document.querySelector(
        '#google-pay-container',
      ) as HTMLElement;

      if (googleButton) {
        googleButton.style.display = 'flex';
        googleButton.style.width = '100%';
      }
    }

    if (showApplePay && showGooglePay) {
      const expressContainer = getVisibleElementById<'div'>(
        'express-payment-container',
      );

      if (expressContainer) {
        expressContainer.style.display = 'block';
      }
    }

    const appleGoogleContainer = document.querySelector(
      '#google-apple-pay-container',
    ) as HTMLElement;

    if (appleGoogleContainer) {
      appleGoogleContainer.style.display = 'flex';
    }
  };

  static getCountriesArray = () => {
    const countries = Country.getAllCountries();
    const brand = window.BRAND;
    const allowedCodes =
      ALLOWED_COUNTRIES[brand] || ALLOWED_COUNTRIES['default'];

    const mapped = countries
      .map(c => ({
        name: c.name,
        code: c.isoCode,
      }))
      .filter(c => allowedCodes.includes(c.code));

    // Add Deutschland if DE is in the allowed list and not already included
    if (allowedCodes.includes('DE') && !mapped.some(c => c.code === 'DE')) {
      mapped.push({
        name: 'Deutschland',
        code: 'DE',
      });
    }

    return mapped;
  };

  static getCountryStates = async (code: string) => {
    const states = await Api.fetchStates(code);
    return states.map(s => ({
      name: s.name,
      code: s.isoCode,
    }));
  };

  static getCountryPhoneCode = (code: string) => {
    const country = Country.getCountryByCode(code);
    return `+${country?.phonecode}`;
  };

  static initOfferCardSelector = (props: OfferCardSelectorProps) => {
    const selectedIdx = props.selectedIdx || 0;
    const offerCards = document.querySelectorAll(`[${props.indexAttribute}]`);

    // Apply initial selection style
    offerCards.forEach(card => {
      const productIndex = card.getAttribute(props.indexAttribute);
      if (productIndex && parseInt(productIndex, 10) === selectedIdx) {
        // Apply selected style
        props.onStyleSelected(card as HTMLElement);
      }
    });

    offerCards.forEach(card => {
      card.addEventListener('click', e => {
        // On click iterate all card and remove stying
        offerCards.forEach(c => props.onRemoveSelected(c as HTMLElement));

        const selectedCard = (e.target as HTMLElement)?.closest(
          `[${props.indexAttribute}]`,
        );
        if (selectedCard) {
          const selectedProductIdx = selectedCard.getAttribute(
            props.indexAttribute,
          );

          // Find all elements with this product index
          const selectedCards = document.querySelectorAll(
            `[productIdx="${selectedProductIdx}"]`,
          );
          // Apply selected style
          selectedCards.forEach(c => props.onStyleSelected(c as HTMLElement));
        }
      });
    });
  };

  private static startTimer = (
    timerEl: HTMLElement,
    defaultTime = '30',
    minutesElementId?: string,
    secondsElemnentId?: string,
  ) => {
    let currentSeconds = parseInt(defaultTime, 10);

    const interval = setInterval(() => {
      currentSeconds--;

      if (currentSeconds < 0) {
        clearInterval(interval);
        currentSeconds = parseInt(defaultTime, 10);
      }

      const minutes = Math.floor(currentSeconds / 60);
      const seconds = currentSeconds - minutes * 60;

      const minutesStr = minutes > 9 ? minutes.toString() : `0${minutes}`;
      const secondsStr = seconds > 9 ? seconds.toString() : `0${seconds}`;

      if (minutesElementId) {
        const minutesEl = timerEl.querySelector(`#${minutesElementId}`);
        if (minutesEl) {
          minutesEl.textContent = minutesStr;
        }
      }
      if (secondsElemnentId) {
        const secondsEl = timerEl.querySelector(`#${secondsElemnentId}`);
        if (secondsEl) {
          secondsEl.textContent = secondsStr;
        }
      } else {
        timerEl.textContent = `${minutesStr}:${secondsStr}`;
      }
    }, 1000);

    timerEl.dataset.intervalId = interval.toString();
  };

  static initTimer = (
    defaultTime: string,
    minutesElementId?: string,
    secondsElemnentId?: string,
    timerElementSelector?: string,
  ) => {
    const timers = document.querySelectorAll(
      timerElementSelector || '[timer-initial]',
    );
    timers.forEach(el =>
      UiController.startTimer(
        el as HTMLElement,
        defaultTime,
        minutesElementId,
        secondsElemnentId,
      ),
    );
  };

  static showErrorModal = (message: string) => {
    const modal = document.getElementById('errorModal');
    const messageText = document.getElementById('errorMessage');
    const errorCloseButton = document.getElementById('errorModalButton');

    if (!modal) {
      const error = new Error(
        'FunnelError: ErrorModal Component not found - unable to show user errors',
      );
      Errors.report(error, undefined, false, false);
    }

    if (modal && messageText && errorCloseButton) {
      messageText.innerHTML = message;
      modal.style.display = 'block';
      modal.addEventListener('click', () => {
        modal.style.display = 'none';
        messageText.innerHTML = 'None';
      });
      errorCloseButton.addEventListener('click', () => {
        modal.style.display = 'none';
        messageText.innerHTML = 'None';
      });
    }
  };

  static showPpLoaderModal = (show: boolean) => {
    const modal = document.getElementById('ppModal');

    if (modal) {
      modal.style.display = show ? 'block' : 'none';
    }
  };

  static showModalLoader = (loading: boolean) => {
    const modalLoader = document.getElementById('modalLoader');
    if (modalLoader) {
      if (loading) {
        modalLoader.style.display = 'flex';
      } else {
        modalLoader.style.display = 'none';
      }
    }
  };

  static setButtonLoading = (buttonId: string, loading: boolean) => {
    const btn = getVisibleElementById<'button'>(buttonId);
    if (btn) {
      btn.disabled = loading;
      btn.style.opacity = loading ? '0.6' : '1';
      const loader: HTMLDivElement | null = btn.querySelector('#loader');
      if (loader) {
        loader.style.display = loading ? 'block' : 'none';
      }
    }
  };

  static setButtonDisabled = (buttonId: string, disabled: boolean) => {
    const btn = getVisibleElementById<'button'>(buttonId);
    if (btn) {
      btn.disabled = disabled;
      btn.style.opacity = disabled ? '0.6' : '1';
    }
  };

  static setUpsellButtonLoading = (loading: boolean) => {
    const buttons = document.querySelectorAll(
      '#buyButton',
    ) as NodeListOf<HTMLDivElement>;
    buttons.forEach(b => {
      b.style.opacity = loading ? '0.6' : '1';
      b.style.pointerEvents = loading ? 'none' : 'auto';

      const loader = b.querySelector('#loader') as HTMLDivElement | null;
      if (loader) loader.style.display = loading ? 'block' : 'none';
      const buttonContent = b.querySelectorAll(
        '#buttonContent',
      ) as NodeListOf<HTMLDivElement>;
      if (buttonContent.length > 0)
        buttonContent.forEach(el => {
          el.style.display = loading ? 'none' : 'block';
        });
    });
  };

  static setElementLoading = (id: string, loading: boolean, log?: string) => {
    const elements = document.querySelectorAll(
      `#${id}`,
    ) as NodeListOf<HTMLDivElement>;
    if (log) {
      console.log('log: ', log);
    }
    elements.forEach(element => {
      element.style.opacity = loading ? '0.6' : '1';
      element.style.pointerEvents = loading ? 'none' : 'auto';

      const loader = element.querySelector('#loader') as HTMLDivElement | null;
      if (loader) loader.style.display = loading ? 'block' : 'none';
      const buttonContent = element.querySelectorAll(
        '#buttonContent',
      ) as NodeListOf<HTMLDivElement>;
      if (buttonContent.length > 0)
        buttonContent.forEach(el => {
          el.style.display = loading ? 'none' : 'block';
        });
    });
  };

  static showAvailablePaymentMethods = (
    paymentMethods: KlarnaAvailablePayment[],
  ) => {
    const paymentMethodsContainer =
      document.querySelector('#availablePayments');

    if (!paymentMethodsContainer) {
      console.error('No element found');
      return;
    }

    const dynamicMethods =
      paymentMethodsContainer.querySelectorAll('.dynamic-method');
    dynamicMethods.forEach(method => method.remove());

    paymentMethods.forEach((method: KlarnaAvailablePayment) => {
      const methodContainer = document.createElement('div');
      methodContainer.className = 'available-payment-block dynamic-method';

      const img = document.createElement('img');
      img.src = method.asset_urls.standard;
      img.alt = method.name;

      const span = document.createElement('span');
      span.innerText = method.name;
      span.className = 'payment-method-name';

      span.style.marginLeft = '8px';

      methodContainer.appendChild(img);
      methodContainer.appendChild(span);
      paymentMethodsContainer.appendChild(methodContainer);
    });
  };

  private static createOption = (
    value: string,
    text: string,
    initialValue?: string,
  ) => {
    const option = document.createElement('option');
    option.text = text;
    option.value = value;
    if (value === initialValue) {
      option.selected = true;
    }
    return option;
  };

  static removeNonDefaultOptions = (selectEl: HTMLSelectElement) => {
    if (selectEl && selectEl.options.length > 1) {
      for (let i = selectEl.options.length - 1; i >= 1; i--) {
        selectEl.remove(i);
      }
    }
  };

  static populateDropdown = (
    el: HTMLSelectElement,
    values: Array<Record<string, string> | string>,
    textKey?: string,
    valueKey?: string,
    initialValue?: string,
  ) => {
    values.forEach(v => {
      const text = textKey
        ? (v as Record<string, string>)[textKey]
        : (v as string);
      const value = valueKey
        ? (v as Record<string, string>)[valueKey]
        : (v as string);
      const option = UiController.createOption(value, text, initialValue);
      el.add(option);
    });
  };

  static showDownsell = (
    show: boolean,
    productIdx: number,
    downsellModalId?: string,
  ) => {
    const modalId = downsellModalId || 'downsellModal';
    const modal = document.getElementById(modalId);

    if (modal) {
      modal.style.display = show ? 'flex' : 'none';
      const prevValue = window['productIdx'].value;
      window['productIdx'].value = productIdx;
      if (show) {
        modal.addEventListener('click', e => {
          if (e.target === modal) {
            modal.style.display = 'none';
            window['productIdx'].value = prevValue;
          }
        });
      }
    }
  };

  static initDownsellSelectors = (
    buttonId: string,
    upsellKey: string,
    trackingName: string,
    productIdx: number,
    downsellModalId?: string,
  ) => {
    const downsellOpenButtons = document.querySelectorAll(buttonId);

    if (isUpsellPage(1) && downsellOpenButtons.length === 0) {
      Errors.report(
        new Error(`No downsell buttons found with id: ${buttonId}`),
        undefined,
        false,
        false,
      );
    }
    downsellOpenButtons.forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        UiController.showDownsell(true, productIdx, downsellModalId);
        Gtm.trackPageView(`Downsell${upsellKey}`, trackingName);
      });
    });
  };

  static showNotification = (show: boolean, info?: IFomo) => {
    const notifElement = window['fomoNotif'];

    if (show && info) {
      window['fomoImage'].src = info.fomoImage;
      window['fomoName'].textContent = info.fomoName;
      window['fomoLocation'].textContent = info.fomoLocation;
      window['fomoProduct'].textContent = info.fomoProduct;
      window['fomoMinutes'].textContent = info.fomoMinutes;

      // Remove any fade-out class, add fade-in, and set display to flex
      notifElement.classList.remove('fomo-fade-out');
      notifElement.classList.add('fomo-fade-in');
      notifElement.style.display = 'flex';
    } else {
      // Start the fade-out animation
      notifElement.classList.remove('fomo-fade-in');
      notifElement.classList.add('fomo-fade-out');

      // Set display to none after the animation duration (1s in this example)
      setTimeout(() => {
        notifElement.style.display = 'none';
      }, 1000);
    }
  };

  static initFomoNotification = async (
    productName: string,
    images?: string[],
  ) => {
    const disabledCountries = ['LT'];
    if (!disabledCountries.includes(window.GEO.country)) {
      for (let i = 0; i < 6; i++) {
        await sleep(8000);

        const notif = createFomoNotification(productName, images);
        UiController.showNotification(true, notif);

        // Adjust this sleep to account for the fade-in animation
        await sleep(7000); // 6s + 1s for fade-out
        UiController.showNotification(false);
      }
    }
  };

  static initProductsFomoNotification = async (
    productName: string,
    products: any[],
  ) => {
    const disabledCountries = ['LT'];
    if (!disabledCountries.includes(window.GEO.country)) {
      for (let i = 0; i < 6; i++) {
        await sleep(8000);

        const notif = createProductsFomoNotification(products, productName);
        UiController.showNotification(true, notif);

        // Adjust this sleep to account for the fade-in animation
        await sleep(7000); // 6s + 1s for fade-out
        UiController.showNotification(false);
      }
    }
  };

  static setFreeShipping = () => {
    const freeShipping =
      document.querySelectorAll<HTMLDivElement>('#freeShipping');
    freeShipping.forEach(el => {
      el.style.display = 'flex';
    });
    const standardShipping =
      document.querySelectorAll<HTMLDivElement>('#standardShipping');
    standardShipping.forEach(el => {
      el.style.display = 'none';
    });
  };

  static initCheckboxAddons = async (props: {
    addonCheckboxIds: string[];
    selectedCallback?: (addonIdx: number) => void;
    unselectedCallback?: (addonIdx: number) => void;
    preSelected?: boolean;
  }) => {
    try {
      interface AddonIdx {
        id?: number;
        value: number;
        quantity: number;
      }

      const cookieAddonIdxs = getCookie(`addonIdxs`) || '[]';
      window['addonIdxs'] ??= JSON.parse(cookieAddonIdxs);

      const {
        addonCheckboxIds,
        selectedCallback,
        unselectedCallback,
        preSelected,
      } = props;

      // Ensure window.pageAddons is initialized
      if (!window.pageAddons || !Array.isArray(window.pageAddons)) {
        console.warn('window.pageAddons is not defined or not an array.');
        return;
      }

      // Handle checkboxes
      if (Array.isArray(addonCheckboxIds)) {
        addonCheckboxIds.forEach((addonId: string) => {
          const addonCheckboxes = document.querySelectorAll<HTMLInputElement>(
            `#${addonId}`,
          );

          addonCheckboxes.forEach(checkbox => {
            const addonIdx = checkbox.getAttribute('data-addon-idx');
            if (!addonIdx) return;

            const selectedAddon = window.pageAddons[parseInt(addonIdx, 10)];
            const addonIdxNumber = parseInt(addonIdx, 10);
            const storedValue = getCookie(`addon-${addonIdx}`);
            const isSelected =
              storedValue === 'true' || (preSelected && storedValue === null);

            if (isSelected) {
              checkbox.checked = true;
              addDataParamsToElement(addonCheckboxIds, selectedAddon);
              if (
                !window['addonIdxs'].some(
                  (item: AddonIdx) => item?.value === addonIdxNumber,
                )
              ) {
                window['addonIdxs'].push({
                  value: addonIdxNumber,
                  quantity: '1',
                });
                setCookieWithExpiry(
                  `addonIdxs`,
                  JSON.stringify(window['addonIdxs']),
                  15,
                );
              }
            }

            checkbox.addEventListener('click', (e: Event) => {
              const selected = (e.target as HTMLInputElement).checked;
              setCookieWithExpiry(`addon-${addonIdx}`, selected.toString(), 15);

              addDataParamsToElement(addonCheckboxIds, selectedAddon);

              if (selected) {
                window['addonIdxs'].push({
                  value: addonIdxNumber,
                  quantity: '1',
                });
                setCookieWithExpiry(
                  `addonIdxs`,
                  JSON.stringify(window['addonIdxs']),
                  15,
                );
                selectedCallback?.(addonIdxNumber);
              } else {
                const indexToRemove = window['addonIdxs'].findIndex(
                  (item: AddonIdx) => item?.value === addonIdxNumber,
                );
                if (indexToRemove !== -1) {
                  window['addonIdxs'].splice(indexToRemove, 1);
                  setCookieWithExpiry(
                    `addonIdxs`,
                    JSON.stringify(window['addonIdxs']),
                    15,
                  );
                }
                unselectedCallback?.(addonIdxNumber);
              }

              if (window.DEBUG) {
                console.log(
                  `Addon selected: ${selected}, AddonIdx: ${addonIdxNumber}, Current AddonIdxs:`,
                  window['addonIdxs'],
                );
              }
            });
          });
        });
      }
    } catch (e) {
      console.error('Error in initCheckboxAddons:', e);
      Errors.report(e);
    }
  };

  static initIncrementalAddons = async (props: {
    addonIncreaseButtonIds: string[];
    addonDecreaseButtonIds: string[];
    defaulValue?: number;
    selectedCallback?: (addonIdx: number) => void;
    unselectedCallback?: (addonIdx: number) => void;
  }) => {
    interface AddonIdx {
      id?: number;
      value: number;
      quantity: number;
    }

    try {
      const cookieAddonIdxs = getCookie(`addonIdxs`) || '[]';
      window['addonIdxs'] ??= JSON.parse(cookieAddonIdxs);

      const {
        addonIncreaseButtonIds,
        addonDecreaseButtonIds,
        selectedCallback,
        unselectedCallback,
      } = props;

      // Ensure window.pageAddons is initialized
      if (!window.pageAddons || !Array.isArray(window.pageAddons)) {
        console.warn('window.pageAddons is not defined or not an array.');
        return;
      }

      // Handle increase buttons
      if (Array.isArray(addonIncreaseButtonIds)) {
        addonIncreaseButtonIds.forEach(buttonId => {
          const increaseButtons = document.querySelectorAll<HTMLButtonElement>(
            `#${buttonId}`,
          );
          increaseButtons.forEach(button => {
            button.addEventListener('click', () => {
              const hasRange =
                button.hasAttribute('data-addon-min-idx') &&
                button.hasAttribute('data-addon-max-idx');
              let callbackValue: number = 0;
              let selectedAddon = window.pageAddons[callbackValue];

              if (hasRange) {
                const minIdx = parseInt(
                  button.getAttribute('data-addon-min-idx') || '0',
                  10,
                );
                const maxIdx = parseInt(
                  button.getAttribute('data-addon-max-idx') || '0',
                  10,
                );

                // Filter items in window['addonIdxs'] that are within the range
                const validItems = window['addonIdxs'].filter(
                  (item: AddonIdx) =>
                    item && item.value >= minIdx && item.value <= maxIdx,
                );

                if (validItems.length > 0) {
                  const highestValue = Math.max(
                    ...validItems.map((item: AddonIdx) => item.value),
                  );
                  if (highestValue < maxIdx) {
                    // Try to find an item with value (highestValue + 1)
                    const existingNext = window['addonIdxs'].find(
                      (item: AddonIdx) => item.value === highestValue + 1,
                    );
                    if (existingNext) {
                      // Increase its quantity
                      existingNext.quantity += 1;
                      callbackValue = existingNext.value;
                      selectedAddon = window.pageAddons[callbackValue];
                    } else {
                      // "Move" the item with highestValue: update its value and increase its quantity by 1
                      const indexToUpdate = window['addonIdxs'].findIndex(
                        (item: AddonIdx) => item.value === highestValue,
                      );
                      window['addonIdxs'][indexToUpdate].value =
                        highestValue + 1;
                      window['addonIdxs'][indexToUpdate].quantity += 1;
                      callbackValue = highestValue + 1;
                      selectedAddon = window.pageAddons[callbackValue];
                    }
                  } else {
                    // Already at max: just increase the quantity of the item with maxIdx
                    const index = window['addonIdxs'].findIndex(
                      (item: AddonIdx) => item.value === maxIdx,
                    );
                    if (index >= 0) {
                      window['addonIdxs'][index].quantity += 1;
                      callbackValue = maxIdx;
                      selectedAddon = window.pageAddons[callbackValue];
                    }
                  }
                } else {
                  // No item in range: add a new one starting at the minimum value
                  window['addonIdxs'].push({ value: minIdx, quantity: 1 });
                  callbackValue = minIdx;
                  selectedAddon = window.pageAddons[callbackValue];
                }
                setCookieWithExpiry(
                  `addonIdxs`,
                  JSON.stringify(window['addonIdxs']),
                  15,
                );
              } else {
                // Non-range: update based on unique identifier
                const addonIdx = parseInt(
                  button.getAttribute('data-addon-idx') || '0',
                  10,
                );
                const index = window['addonIdxs'].findIndex(
                  (item: AddonIdx) => item && item.id === addonIdx,
                );
                if (index >= 0) {
                  window['addonIdxs'][index].quantity += 1;
                  callbackValue = addonIdx;
                  selectedAddon = window.pageAddons[callbackValue];
                } else {
                  window['addonIdxs'].push({
                    id: addonIdx,
                    value: addonIdx,
                    quantity: 1,
                  });
                  callbackValue = addonIdx;
                  selectedAddon = window.pageAddons[callbackValue];
                }
                setCookieWithExpiry(
                  `addonIdxs`,
                  JSON.stringify(window['addonIdxs']),
                  15,
                );
              }

              addDataParamsToElement(addonIncreaseButtonIds, selectedAddon);
              setCookieWithExpiry(
                `addon-count`,
                window['addonIdxs'].length.toString(),
                15,
              );
              selectedCallback?.(callbackValue);
            });
          });
        });
      }

      // Handle decrease buttons
      if (Array.isArray(addonDecreaseButtonIds)) {
        addonDecreaseButtonIds.forEach(buttonId => {
          const decreaseButtons = document.querySelectorAll<HTMLButtonElement>(
            `#${buttonId}`,
          );
          decreaseButtons.forEach(button => {
            button.addEventListener('click', () => {
              const hasRange =
                button.hasAttribute('data-addon-min-idx') &&
                button.hasAttribute('data-addon-max-idx');
              let callbackValue: number = 0;
              let selectedAddon = window.pageAddons[callbackValue];

              if (hasRange) {
                const minIdx = parseInt(
                  button.getAttribute('data-addon-min-idx') || '0',
                  10,
                );
                const maxIdx = parseInt(
                  button.getAttribute('data-addon-max-idx') || '0',
                  10,
                );

                // Filter items in window['addonIdxs'] that are within the range
                const validItems = window['addonIdxs'].filter(
                  (item: AddonIdx) =>
                    item && item.value >= minIdx && item.value <= maxIdx,
                );

                if (validItems.length > 0) {
                  const lowestValue = Math.min(
                    ...validItems.map((item: AddonIdx) => item.value),
                  );
                  const indexToUpdate = window['addonIdxs'].findIndex(
                    (item: AddonIdx) => item.value === lowestValue,
                  );
                  if (lowestValue > minIdx) {
                    // Instead of resetting quantity to 1, we decrease the quantity by 1
                    if (window['addonIdxs'][indexToUpdate].quantity > 1) {
                      window['addonIdxs'][indexToUpdate].quantity -= 1;
                      // "Move" the item down one level:
                      window['addonIdxs'][indexToUpdate].value =
                        lowestValue - 1;
                    } else {
                      // If quantity is 1, update its value to min (or remove it if desired)
                      window['addonIdxs'][indexToUpdate].value =
                        lowestValue - 1;
                      // Optionally, you might want to remove the item if quantity is 1. Adjust as needed.
                    }
                    callbackValue = lowestValue - 1;
                    selectedAddon = window.pageAddons[callbackValue];
                  } else {
                    // At minimum level: remove the item entirely.
                    callbackValue = minIdx;
                    selectedAddon = window.pageAddons[callbackValue];
                    window['addonIdxs'].splice(indexToUpdate, 1);
                  }
                }
                setCookieWithExpiry(
                  `addonIdxs`,
                  JSON.stringify(window['addonIdxs']),
                  15,
                );
              } else {
                // For non-range buttons, remove the object based on its unique identifier.
                const addonIdx = parseInt(
                  button.getAttribute('data-addon-idx') || '0',
                  10,
                );
                const indexToRemove = window['addonIdxs'].findIndex(
                  (item: AddonIdx) => item && item.id === addonIdx,
                );
                if (indexToRemove !== -1) {
                  window['addonIdxs'].splice(indexToRemove, 1);
                  callbackValue = addonIdx;
                  selectedAddon = window.pageAddons[callbackValue];
                }
                setCookieWithExpiry(
                  `addonIdxs`,
                  JSON.stringify(window['addonIdxs']),
                  15,
                );
              }

              addDataParamsToElement(addonDecreaseButtonIds, selectedAddon);
              setCookieWithExpiry(
                `addon-count`,
                window['addonIdxs'].length.toString(),
                15,
              );
              unselectedCallback?.(callbackValue);
            });
          });
        });
      }
    } catch (e) {
      console.error('Error in initIncrementalAddons:', e);
      Errors.report(e);
    }
  };

  initDynamicYear = (elementId = 'current-year', elementIds?: string[]) => {
    if (elementIds && Array.isArray(elementIds)) {
      elementIds.forEach(id => {
        document.querySelectorAll(`#${id}`).forEach(element => {
          if (element) {
            element.textContent = `${new Date().getFullYear()}`;
          }
        });
      });
    } else {
      document.querySelectorAll(`#${elementId}`).forEach(element => {
        if (element) {
          element.textContent = `${new Date().getFullYear()}`;
        }
      });
    }
  };
}
